import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { Spinner } from "../../../components";
import ApiService from "../../../service/ApiService";
import AlertService from "../../../service/AlertService";
import { DatabaseEntities } from "../../../model/enum/database-entities.enum";
import FirebaseService from "../../../service/FirebaseService";
import DeleteModal from "../../modal/DeleteModal";
import Bundle from "../../../model/bundle";
import Product from "../../../model/product";
import Pathnames from "../../../model/enum/Pathnames";
import ToastMessages from "../../../model/enum/toast-messages.enum";
import EditIcon from "../../../assets/admin/edit-icon.svg";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from "../../../assets/admin/upload-icon.svg";
import PageResultResponse from "../../../model/page-result-response";
import Select, { StylesConfig } from "react-select";
import Misc from "../../../utils/Misc";
import useStyles from "./styles";
import "../../styles/tableStyles.css";
import {
  DarkGrey,
  LightPrimaryColor,
  PrimaryColor,
  White,
} from "../../../shared/styles/colors";

const style = {
  position: "absolute" as "absolute",
  top: "50%", // Start from a fixed top position
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

const styles: StylesConfig<any, true> = {
  container: (base) => ({
    ...base,
    width: "95% !important",
    borderColor: DarkGrey,
    borderRadius: "5px",
    marginLeft: "10px",
  }),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? PrimaryColor : DarkGrey,
    boxShadow: state.isFocused ? LightPrimaryColor : base.boxShadow,
    "&:hover": {
      borderColor: PrimaryColor,
    },
    "&:selected": {
      borderColor: PrimaryColor,
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? LightPrimaryColor
      : state.isSelected
        ? PrimaryColor
        : base.backgroundColor,
    color: state.isFocused || state.isSelected ? White : base.color,
  }),
  menu: (base) => ({
    ...base,
  }),
};

type Item = {
  id: number;
  productId: number | "";
  quantity: number;
};

const BundleModal = ({ bundle }: any) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const navigate = useNavigate();

  const edit = bundle != null;
  const [name, setName] = useState(edit ? (bundle.name ?? bundle.task) : null);
  const [description, setDescription] = useState(
    edit ? bundle.description : null,
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [items, setItems] = useState<Item[]>(edit ? bundle.BundleItems : []);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [fileName, setFileName] = useState("");
  const imageUrl = edit ? bundle.imageUrl : null;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await ApiService.get<PageResultResponse>(
        `${DatabaseEntities.PRODUCTS}?order=name`,
      )
        .then((data) => {
          setProducts(data.items);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (open) fetchData();
  }, [open]);

  const handleName = (e: any) => setName(e.target.value);
  const handleDescription = (e: any) => setDescription(e.target.value);

  const handleFileChange = (e: any) => {
    setFile(e.target.files[0]);
    setFileName(e.target.files[0].name);
  };

  const handleAddItem = () => {
    const newItem: Item = {
      id: items.length + 1,
      productId: "", // Adjusting type for productService
      quantity: 0,
    };
    setItems([...items, newItem]);
  };

  const handleProduct = (e: any, index: number) => {
    if (!e || !e.value) {
      const updatedItems = [...items];
      updatedItems[index].productId = "";
      setItems(updatedItems);
      return;
    }

    const updatedItems = [...items];
    updatedItems[index].productId = Number(e.value);
    setItems(updatedItems);
  };

  const handleQuantity = (e: any, index: number) => {
    const updatedItems = [...items];
    updatedItems[index].quantity = parseInt(e.target.value) || 0;
    setItems(updatedItems);
  };

  const handleDeleteItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const resetList = () => {
    setTimeout(() => {
      navigate(Pathnames.PRODUCTS_SERVICES_BUNDLE_LIST);
    }, 3000);
  };

  const handleCreate = async (payload: Bundle) => {
    if (file) {
      await FirebaseService.uploadFile(file, fileName, "Bundle")
        .then(async (url: string) => {
          payload.imageUrl = url;
        })
        .catch(() => {
          AlertService.error(ToastMessages.UPLOAD_FAILED);
        });
    }

    await ApiService.post<Bundle>(DatabaseEntities.BUNDLES, payload)
      .then(() => {
        AlertService.success(ToastMessages.CREATED);
        resetList();
        setName(null);
        setDescription(null);
        setFile(undefined);
        setItems([]);
      })
      .catch((error: any) => {
        console.error("Error creating bundle:", error);
        AlertService.error(`${ToastMessages.CREATE_FAILED}: ${error.message}`);
      });
  };

  const handleUpdate = async (payload: Bundle) => {
    await ApiService.put<Bundle>(
      `${DatabaseEntities.BUNDLES}/${payload.id}`,
      payload,
    )
      .then(() => {
        AlertService.success(ToastMessages.UPDATED);
        resetList();
      })
      .catch(() => {
        AlertService.error(ToastMessages.UPDATE_FAILED);
      });
  };

  const handleDelete = async (payload: Bundle) => {
    await ApiService.delete<any>(`${DatabaseEntities.BUNDLES}/${payload.id}`)
      .then(() => {
        AlertService.success(ToastMessages.DELETED);
        resetList();
      })
      .catch(() => {
        AlertService.error(ToastMessages.DELETE_FAILED);
      });
  };

  const invalidInput = (): boolean => {
    if (Misc.isNullOrEmpty(name) || items.length === 0) return true;

    // Confirm if all the items has a productId and a quantity
    if (items.length > 0)
      return (
        items.some((x) => x.productId == "") ||
        items.some((x) => x.quantity == 0)
      );

    return false;
  };

  const submit = async () => {
    if (invalidInput()) return;

    setLoading(true);

    const payload: Bundle = {
      id: edit ? bundle.id : 0,
      name: name,
      description: description,
      items: items,
      isUltra: false,
      imageUrl: imageUrl,
    };

    if (edit) await handleUpdate(payload);
    else await handleCreate(payload);

    setLoading(false);
    handleClose();
  };

  return (
    <div>
      {edit ? (
        <Button onClick={handleOpen}>
          <img src={EditIcon} alt="edit icon" />
        </Button>
      ) : (
        <div style={{ cursor: "pointer" }} onClick={handleOpen}>
          <Typography className={classes.subTitle}>Bundle</Typography>
          <Typography>
            This modal allows you to create a new bundle by grouping multiple
            products or services, defining its details for better organization
            and management.
          </Typography>
        </div>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <Box sx={style}>
            <div className={classes.headerDiv}>
              {edit ? (
                <Typography className={classes.title}>
                  <DeleteModal item={bundle} handleDelete={handleDelete} />
                </Typography>
              ) : (
                <Typography className={classes.title}>Create bundle</Typography>
              )}
              <Button onClick={handleClose} sx={{ color: "black" }}>
                <CloseIcon />
              </Button>
            </div>
            <hr />
            <div className={classes.form}>
              <Typography className={classes.label}>
                Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <input
                className={classes.input}
                value={name}
                onChange={handleName}
                placeholder="Add text..."
              />
              <Typography className={classes.label}>Description</Typography>
              <textarea
                className={classes.input}
                value={description}
                onChange={handleDescription}
                placeholder="Add a description.."
              />
              {!edit && (
                <>
                  <div className={classes.inputContainer}>
                    <Typography className={classes.label}>Image</Typography>
                  </div>
                  <div className={classes.uploadInput}>
                    <input
                      type="file"
                      id="add-single-img"
                      accept="image/*"
                      className="d-none"
                      onChange={handleFileChange}
                    />
                    <label id="image-label" htmlFor="add-single-img">
                      <img
                        src={UploadIcon}
                        className={classes.image}
                        alt="upload icon"
                      />
                      <label>Drop files to upload or browse</label>
                      <Typography>{fileName}</Typography>
                    </label>
                  </div>
                </>
              )}
              <Typography style={{ marginTop: "20px", marginBottom: "10px" }}>
                Products/Services included into the bundle{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <div className="table-container">
                <table className="styled-table">
                  <thead>
                    <td>Product/Service</td>
                    <td>Quantity</td>
                    <td>Action</td>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={item.id}>
                        <td>
                          <Select
                            styles={styles}
                            value={
                              item.productId
                                ? {
                                    value: item.productId,
                                    label: `${
                                      products.find(
                                        (clt: Product) =>
                                          clt.id === item.productId,
                                      )?.name
                                    }`,
                                  }
                                : ""
                            }
                            isClearable={true}
                            onChange={(e) => handleProduct(e, index)}
                            options={products.map((prod: Product) => ({
                              value: prod.id,
                              label: `${prod.name}`,
                            }))}
                            placeholder="Select product/service"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            placeholder="01"
                            min={1}
                            max={99}
                            value={items[index].quantity}
                            onChange={(e) => handleQuantity(e, index)}
                          />
                        </td>
                        <td>
                          <Button
                            className="delete-btn"
                            onClick={() => handleDeleteItem(index)}
                          >
                            🗑️
                          </Button>
                        </td>
                      </tr>
                    ))}
                    <tr className="add-item-row">
                      <td
                        colSpan={3}
                        className="add-item"
                        onClick={handleAddItem}
                      >
                        <span>+ Add Item</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className={classes.buttonDiv}>
              <Btn
                className={classes.button}
                onClick={submit}
                disabled={invalidInput()}
              >
                {edit ? "Save" : "Create"}
              </Btn>
            </div>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default BundleModal;
